import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Eraser, Download, X, Square, RotateCcw, ShieldOff, Sparkles, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/config";
import PDFAnonymizer from "./PDFAnonymizer";

/**
 * Client-side CV anonymization tool. Loads the document (image only),
 * lets the admin draw redaction rectangles over any sensitive area,
 * and exports the flattened image with black rectangles as JPEG.
 *
 * PDFs are NOT supported in this v1 (would need pdf-lib or client-side rasterization).
 *
 * Props:
 *   doc: { doc_id, original_filename, mime_type? } — the document to anonymize
 *   candidateName: string — display only, used in filename
 *   open, onOpenChange: dialog state
 *   onSaved: (newDocId) => void — optional, called after a successful save
 *     (PDF or image path) so the parent can refresh the candidate's document list
 */
export default function CVAnonymizer({ doc, candidateName = "candidat", open, onOpenChange, onSaved }) {
  const [imgSrc, setImgSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rects, setRects] = useState([]); // {x, y, w, h}
  const [drawing, setDrawing] = useState(null); // {startX, startY}
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [aiDetecting, setAiDetecting] = useState(false);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  const filename = doc?.original_filename || "";
  const isImage = filename && /\.(jpe?g|png|webp|heic)$/i.test(filename);
  const isPdf = (filename && /\.pdf$/i.test(filename)) || doc?.content_type === "application/pdf";

  // Load doc as blob → object URL
  useEffect(() => {
    if (!open || !doc?.doc_id || !isImage) return;
    setLoading(true);
    setError("");
    setImgSrc(null);
    setRects([]);
    let url = null;
    let cancelled = false;
    axios.get(`${API}/documents/${doc.doc_id}/download`, { withCredentials: true, responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setImgSrc(url);
      })
      .catch(() => { if (!cancelled) setError("Impossible de charger l'image"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [open, doc?.doc_id, isImage]);

  // Draw the canvas whenever the image OR rectangles change
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img || !img.complete) return;
    const ctx = canvas.getContext("2d");
    // Full-resolution canvas backing the image
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
    ctx.drawImage(img, 0, 0);
    ctx.fillStyle = "#000";
    rects.forEach((r) => {
      ctx.fillRect(r.x, r.y, r.w, r.h);
    });
  }, [rects]);

  useEffect(() => { redraw(); }, [redraw, imgSrc]);

  const onImgLoad = () => { redraw(); };

  const getCanvasCoords = (evt) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    // scale between displayed size and internal buffer size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (evt.clientX - rect.left) * scaleX,
      y: (evt.clientY - rect.top) * scaleY,
    };
  };

  const onMouseDown = (e) => {
    if (!canvasRef.current) return;
    const { x, y } = getCanvasCoords(e);
    setDrawing({ startX: x, startY: y });
  };

  const onMouseMove = (e) => {
    if (!drawing || !canvasRef.current) return;
    const { x, y } = getCanvasCoords(e);
    const ctx = canvasRef.current.getContext("2d");
    redraw();
    // Preview the current draw
    ctx.strokeStyle = "#662483";
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(
      Math.min(drawing.startX, x),
      Math.min(drawing.startY, y),
      Math.abs(x - drawing.startX),
      Math.abs(y - drawing.startY),
    );
    ctx.setLineDash([]);
  };

  const onMouseUp = (e) => {
    if (!drawing) return;
    const { x, y } = getCanvasCoords(e);
    const rect = {
      x: Math.min(drawing.startX, x),
      y: Math.min(drawing.startY, y),
      w: Math.abs(x - drawing.startX),
      h: Math.abs(y - drawing.startY),
    };
    setDrawing(null);
    if (rect.w > 3 && rect.h > 3) setRects((prev) => [...prev, rect]);
  };

  const clearAll = () => setRects([]);
  const undo = () => setRects((prev) => prev.slice(0, -1));

  const runAiDetect = async () => {
    if (!doc?.doc_id || !isImage) return;
    setAiDetecting(true);
    try {
      const r = await axios.post(`${API}/admin/documents/${doc.doc_id}/detect-pii`, {}, { withCredentials: true });
      const boxes = r.data?.boxes || [];
      const canvas = canvasRef.current;
      if (!canvas || !canvas.width) {
        toast.error("Image non chargée");
        return;
      }
      // Convert normalized [0..1] boxes to canvas pixel coords + merge with existing rects
      const scaled = boxes.map((b) => ({
        x: Math.round(b.x * canvas.width),
        y: Math.round(b.y * canvas.height),
        w: Math.max(2, Math.round(b.w * canvas.width)),
        h: Math.max(2, Math.round(b.h * canvas.height)),
        label: b.label || "PII",
      })).filter((b) => b.w >= 2 && b.h >= 2);
      if (scaled.length === 0) {
        toast.info("L'IA n'a détecté aucune zone à masquer");
        return;
      }
      setRects((prev) => [...prev, ...scaled]);
      toast.success(`${scaled.length} zone${scaled.length > 1 ? "s" : ""} détectée${scaled.length > 1 ? "s" : ""} par l'IA`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur IA");
    } finally { setAiDetecting(false); }
  };

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) { toast.error("Impossible de générer le fichier"); return; }
      const safeName = (candidateName || "candidat").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CV_${safeName}_anonymise.jpg`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`CV anonymisé téléchargé (${rects.length} zone${rects.length > 1 ? "s" : ""} masquée${rects.length > 1 ? "s" : ""})`);
    }, "image/jpeg", 0.92);
  };

  if (!doc) return null;

  // Route PDFs to the dedicated PDFAnonymizer (uses pdfjs-dist to render pages
  // + PyMuPDF server-side to apply true redactions on the PDF).
  if (isPdf) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="rounded-none p-0 max-w-[95vw] h-[92vh] sm:max-w-[1400px] flex flex-col overflow-hidden" data-testid="cv-anonymizer-dialog-pdf">
          <div className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white px-4 py-2.5 flex items-center gap-3 shrink-0">
            <ShieldOff className="w-4 h-4" />
            <DialogTitle className="font-bold text-sm">Anonymisation du PDF — {candidateName}</DialogTitle>
            <DialogDescription className="text-xs opacity-80 hidden md:inline">Rectangles → redaction PyMuPDF (texte réellement supprimé)</DialogDescription>
            <button onClick={() => onOpenChange(false)} className="ml-auto p-1.5 hover:bg-white/15 transition-colors" title="Fermer">
              <X className="w-4 h-4" />
            </button>
          </div>
          <PDFAnonymizer
            doc={doc}
            candidateName={candidateName}
            onSaved={(newDocId) => {
              toast.success("PDF anonymisé sauvegardé dans la fiche candidat");
              onSaved?.(newDocId);
              onOpenChange(false);
            }}
            onClose={() => onOpenChange(false)}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none p-0 max-w-[95vw] h-[92vh] sm:max-w-[1400px] flex flex-col overflow-hidden" data-testid="cv-anonymizer-dialog">
        <div className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white px-4 py-2.5 flex items-center gap-3 shrink-0">
          <ShieldOff className="w-4 h-4" />
          <DialogTitle className="font-bold text-sm">Anonymisation du CV — {candidateName}</DialogTitle>
          <DialogDescription className="text-xs opacity-80 hidden md:inline">Dessinez des rectangles sur les zones à masquer (nom, téléphone, adresse…)</DialogDescription>
          <button onClick={() => onOpenChange(false)} className="ml-auto p-1.5 hover:bg-white/15 transition-colors" title="Fermer" data-testid="cv-anonymizer-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="bg-white border-b border-gray-200 px-3 py-2 flex items-center gap-2 shrink-0 flex-wrap" data-testid="cv-anonymizer-toolbar">
          <Square className="w-3.5 h-3.5 text-[#662483]" />
          <span className="text-xs text-gray-600">Cliquer-glisser pour masquer une zone</span>
          <span className="text-xs font-bold text-[#662483] ml-2" data-testid="cv-anonymizer-rect-count">{rects.length} zone{rects.length > 1 ? "s" : ""}</span>
          <div className="flex-1" />
          <Button
            onClick={runAiDetect}
            disabled={aiDetecting || !imgSrc}
            className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none text-xs h-8"
            data-testid="cv-anonymizer-ai-detect"
            title="Laisser Claude Vision détecter les PII et pré-remplir les rectangles"
          >
            {aiDetecting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            {aiDetecting ? "Analyse..." : "Détecter auto (IA)"}
          </Button>
          <Button onClick={undo} disabled={rects.length === 0} variant="outline" className="rounded-none text-xs h-8" data-testid="cv-anonymizer-undo">
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Annuler la dernière
          </Button>
          <Button onClick={clearAll} disabled={rects.length === 0} variant="outline" className="rounded-none text-xs h-8" data-testid="cv-anonymizer-clear">
            <Eraser className="w-3.5 h-3.5 mr-1" /> Tout effacer
          </Button>
          <Button
            onClick={handleDownload}
            disabled={!imgSrc}
            className="bg-[#0069B4] hover:bg-[#0069B4]/90 text-white rounded-none text-xs h-8"
            data-testid="cv-anonymizer-download"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> Télécharger anonymisé
          </Button>
        </div>

        {/* Canvas area */}
        <div ref={containerRef} className="flex-1 overflow-auto bg-[#FAFAFA] flex items-center justify-center p-4">
          {!isImage && (
            <div className="text-center max-w-md">
              <ShieldOff className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-sm font-semibold text-[#3F3F3F] mb-2">Anonymisation disponible pour les images uniquement</p>
              <p className="text-xs text-gray-600 mb-4">
                Ce document est en <strong className="uppercase">{(doc.original_filename || "").split(".").pop() || "PDF"}</strong>.
                L&apos;anonymisation manuelle par rectangles fonctionne uniquement sur des images (JPG, PNG, WEBP).
              </p>
              <div className="bg-[#F0E9F3] border-l-4 border-[#662483] p-3 text-left text-xs text-[#3F3F3F] mb-3">
                <p className="font-bold mb-1">Solutions :</p>
                <ol className="list-decimal ml-4 space-y-1">
                  <li>Ouvrez le PDF, faites une capture d&apos;écran de la page à anonymiser (ou exportez-la en JPG).</li>
                  <li>Déposez cette image comme nouveau CV via le bouton « Déposer un document » de la conformité.</li>
                  <li>Rouvrez ici pour tracer les rectangles à masquer.</li>
                </ol>
              </div>
              <p className="text-[11px] text-gray-500 italic">💡 Astuce : les CV mobiles récents sont souvent en JPG, l&apos;anonymisation fonctionnera directement.</p>
            </div>
          )}
          {isImage && loading && <p className="text-sm text-gray-500">Chargement…</p>}
          {isImage && error && <p className="text-sm text-red-600">{error}</p>}
          {isImage && imgSrc && (
            <div className="relative inline-block">
              {/* Hidden reference image used to seed the canvas */}
              <img ref={imageRef} src={imgSrc} alt="cv" onLoad={onImgLoad} className="hidden" />
              <canvas
                ref={canvasRef}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={() => setDrawing(null)}
                className="max-w-full max-h-[calc(92vh-140px)] shadow-md cursor-crosshair"
                style={{ imageRendering: "auto" }}
                data-testid="cv-anonymizer-canvas"
                aria-label={`Canvas d'anonymisation du CV de ${candidateName}, ${imgSize.w}x${imgSize.h} pixels`}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
